import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { get, isEmpty } from "lodash";
import { withTranslation, useTranslation } from "react-i18next";
import moment from "moment";
import { usePreCheckInContext } from "./contexts/PreCheckInContext";
import BookingInfoImage from "./BookingInfoImage";
import defaultAvatar from "../../assets/precheckin/avatarDefaultMale0.png";
import ReservationService from "../../backend/ReservationService";
import Guests from "../../model/guests";
import Property from "../../model/property";
import { ClipLoaderIcon } from "../../components/LoadingIcon";
import { convertToAmPm } from "../../utils/timeUtils";

const GuestListManagement = () => {
  const {
    loading,
    propertyUid,
    bookingNumber,
    language,
    lastName,
    userEmail,
    resetBooking,
    data: {
      property,
      guestService,
      configuration,
      presets,
      accessToken,
      booking,
      guests,
    },
    func: {
      updateGuests,
      resetBookingData,
      updateErrorModal,
      setAccessTokenData,
      clearLocalStorage,
    },
    components: { PreCheckInHeader, PreCheckInContainer },
  } = usePreCheckInContext();

  const history = useHistory();
  const { t } = useTranslation();
  const [allGuests, setAllGuests] = useState([]);
  const [isPageMounted, setIsPageMounted] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);

  useEffect(() => {
    const unloadCallback = (event) => {
      event.preventDefault();
      if (!event.returnValue) {
        const histories = [...Property.getHistoryStack()];
        clearLocalStorage();
        Property.setHistoryStack(histories);
        event.returnValue = "";
      }
      return "";
    };

    window.addEventListener("beforeunload", unloadCallback);
    return () => window.removeEventListener("beforeunload", unloadCallback);
  }, [clearLocalStorage]);

  const goToBookDetail = useCallback(() => {
    history.push(
      `/pre-checkin/${propertyUid}/book-detail/${encodeURIComponent(
        bookingNumber
      )}/${encodeURIComponent(
        lastName
      )}?language=${language}&email=${encodeURIComponent(userEmail)}`
    );
  }, [propertyUid, bookingNumber, lastName, language, userEmail, history]);

  const goToAddYourselfToTrip = useCallback(() => {
    history.push(
      `/pre-checkin/${propertyUid}/guest-list-management/${encodeURIComponent(
        bookingNumber
      )}/${encodeURIComponent(
        lastName
      )}/add-yourself-to-trip?language=${language}&email=${encodeURIComponent(
        userEmail
      )}`
    );
  }, [propertyUid, bookingNumber, lastName, language, userEmail, history]);

  useEffect(() => {
    if (pageLoading) {
      if (submitted && resetBooking) {
        setSubmitted(false);
      } else if (!submitted && !resetBooking && !isEmpty(booking)) {
        setPageLoading(false);
      }
    }
  }, [pageLoading, resetBooking, booking, submitted]);

  useEffect(() => {
    if (!loading) {
      if (guestService && !guestService.enableGuestListManagement) {
        return goToBookDetail();
      }
      if (guests) {
        const areAllGuestsNotEditable = [...guests.allGuests()].every(
          (guest) => !guest.editable
        );
        if (areAllGuestsNotEditable) return goToAddYourselfToTrip();
      }
    }
  }, [loading, guestService, goToBookDetail, guests, goToAddYourselfToTrip]);

  useEffect(() => {
    if (
      guestService &&
      guestService.enableGuestListManagement &&
      isPageMounted &&
      guests
    ) {
      setAllGuests([...guests.allGuests()]);
      return () => setIsPageMounted(false);
    }
  }, [isPageMounted, guestService, guests]);

  const removeBtn = (item) => {
    setSubmitted(true);
    keepGuestDataAndMove(item);
    if (!presets.demoMode) {
      requestCreateAdditionalGuest(prepareData(item));
    }
  };

  const keepGuestDataAndMove = ({ guest, isChild }) => {
    const updatedGuests = allGuests;
    const guestId = guest.guestId || guest.guest_id;
    if (isChild) {
      const adultIndex = updatedGuests.findIndex((guest) =>
        guest.children.some(
          (child) => (child.guestId || child.guest_id) === guestId
        )
      );
      if (adultIndex !== -1) {
        const childIndex = updatedGuests[adultIndex].children.findIndex(
          (child) => (child.guestId || child.guest_id) === guestId
        );
        if (childIndex !== -1) {
          updatedGuests[adultIndex].children.splice(childIndex, 1);
        }
      }
    } else {
      const index = updatedGuests.findIndex((item) => item.guestId === guestId);
      if (index !== -1) {
        updatedGuests.splice(index, 1);
      }
    }
    setAllGuests(updatedGuests);
    updateGuests(new Guests(updatedGuests));
  };

  const requestCreateAdditionalGuest = async ({ reservationId, guestIds }) => {
    setPageLoading(true);
    window.scrollTo(0, 0);
    await ReservationService.removeAdditionalGuest(
      accessToken,
      propertyUid,
      reservationId,
      guestIds
    )
      .then(() => {
        setPageLoading(false);
      })
      .catch((error) => {
        if (get(error.response, "status") === 401) {
          setAccessTokenData();
        }
        console.error(error);
        if (
          error.response &&
          error.response.data &&
          error.response.data.error
        ) {
          updateErrorModal(true, error.response.data.error);
        } else {
          updateErrorModal(true, "An unexpected error occurred.");
        }
        setPageLoading(false);
        resetBookingData();
      });
  };

  const prepareData = ({ guest }) => {
    const reservationId = get(booking, "resv_id");
    const guestId = guest.guestId || guest.guest_id;
    const guestIds = [guestId];
    return { reservationId, guestIds };
  };

  const enableRegisterDuringWindowTime = () => {
    if (!configuration?.notAllowedPreCheckInAfterCheckTime || !booking)
      return true;

    const checkInTime = configuration.checkInTimeText.replace("--:--", "00:00");
    const checkInFormat = booking.checkInFormatDate;
    const checkInDateTimeFormat = new Date(
      `${checkInFormat} ${checkInTime}:00`
    );
    const checkInDateTime = moment(checkInDateTimeFormat);
    const preCheckInWindowTime = configuration.preCheckInWindow || "";
    let checkInWindowTime;
    if (preCheckInWindowTime === "same_day" || preCheckInWindowTime === "24h") {
      checkInWindowTime = checkInDateTime.subtract({
        hours: "24",
        minutes: "00",
      });
    } else if (preCheckInWindowTime === "36h") {
      checkInWindowTime = checkInDateTime.subtract({
        hours: "36",
        minutes: "00",
      });
    } else if (preCheckInWindowTime === "48h") {
      checkInWindowTime = checkInDateTime.subtract({
        hours: "48",
        minutes: "00",
      });
    } else if (preCheckInWindowTime === "72h") {
      checkInWindowTime = checkInDateTime.subtract({
        hours: "72",
        minutes: "00",
      });
    } else if (preCheckInWindowTime === "30_days") {
      checkInWindowTime = checkInDateTime.subtract(30, "days");
    } else {
      checkInWindowTime = moment();
    }
    const currentTime = moment();
    return (
      currentTime.isBetween(
        checkInWindowTime,
        checkInDateTime,
        undefined,
        "[]"
      ) || false
    );
  };

  const renderGuestList = (items) => {
    const item = items && items.item;
    if (!item) {
      return null;
    }
    if (
      !items.isChild &&
      ((item.mainGuest && item.mainGuest === items.invitationList) ||
        (!item.mainGuest && item.accountExist === items.invitationList) ||
        (isEmpty(item.email) && item.ageGroup !== "ADULT"))
    ) {
      return null;
    }
    let guestName = `${item.firstName} ${item.lastName}`;
    if (items.isChild) guestName = `${item.first_name} ${item.last_name}`;
    const guestEmail = item && item.email;
    const arrayOfAdditionalGuestChildren = (item && item.children) || [];
    const readyPreCheckIn = booking.status === "ready_to_pre_checkin";
    return (
      <>
        <div
          style={{ flex: 1, marginBottom: "10px" }}
          className="flex space-x-4 items-center justify-between">
          {item.preCheckedInCompleted && guestEmail ? (
            <div className="w-28 flex-shrink-0 relative">
              <img
                src={get(item, "avatar.thumbnail.url", defaultAvatar)}
                alt="avatar"
                className="rounded-full w-28 h-28 object-cover border-2"
                style={{
                  borderColor: "#32C33D",
                }}
              />
              <span className="absolute right-0 bottom-0">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="#32C33D"
                  className="w-12 h-12">
                  <path
                    fillRule="evenodd"
                    d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z"
                    clipRule="evenodd"
                  />
                </svg>
              </span>
            </div>
          ) : (
            <div className="w-28 flex-shrink-0">
              <img
                src={get(item, "avatar.thumbnail.url", defaultAvatar)}
                alt="avatar"
                className="rounded-full w-28 h-28 object-cover"
              />
            </div>
          )}
          <div
            className="flex flex-col flex-1"
            style={{ lineBreak: "anywhere" }}>
            <p className="mb-1">{guestName}</p>
            {item.mainGuest ? (
              <button
                className={`bg-white border w-36 py-1 type-title normal capitalize ${presets.buttonStyleName}`}
                style={{
                  color: presets.appHomeTextDetailsPrecheckin,
                  backgroundColor: presets.backgroundColorTopBar,
                }}>
                {t("PRIMARY")}
              </button>
            ) : !items.isChild && guestEmail ? (
              <p className="break-words">{guestEmail}</p>
            ) : item.ageGroup === "ADULT" ? (
              <></>
            ) : (
              <button
                className={`bg-white border w-24 py-1 type-title normal capitalize ${presets.buttonStyleName}`}
                style={{
                  color: presets.backgroundColorTopBar,
                  borderColor: presets.backgroundColorTopBar,
                }}>
                {t("CHILD")}
              </button>
            )}
          </div>
          <div className="flex-shrink-0">
            {item.preCheckedInCompleted ? (
              <button
                className={`bg-white border px-4 w-36 py-2 type-title normal cursor-not-allowed capitalize ${presets.buttonStyleName}`}
                style={{
                  color: "#595959",
                  borderColor: "#595959",
                }}>
                {t("REGISTERED")}
              </button>
            ) : item.currentGuest && readyPreCheckIn ? (
              enableRegisterDuringWindowTime() && (
                <button
                  className={`bg-white border px-4 w-36 py-2 type-title normal capitalize ${presets.buttonStyleName}`}
                  style={{
                    color: presets.appHomeTextDetailsPrecheckin,
                    backgroundColor: presets.backgroundColorTopBar,
                  }}
                  onClick={() =>
                    item.mainGuest ? registerGuestPage(false) : goToBookDetail()
                  }>
                  {t("REGISTER")}
                </button>
              )
            ) : !item.currentGuest && booking.mainGuestIsPrimaryGuest ? (
              <button
                className={`bg-white border px-4 w-36 py-2 type-title normal capitalize ${presets.buttonStyleName}`}
                style={{
                  color: presets.appHomeTextDetailsPrecheckin,
                  backgroundColor: "#B5B6B7",
                }}
                onClick={() =>
                  removeBtn({ guest: item, isChild: items.isChild })
                }>
                {t("REMOVE")}
              </button>
            ) : (
              ""
            )}
          </div>
        </div>
        {arrayOfAdditionalGuestChildren &&
          arrayOfAdditionalGuestChildren.map((item, index) => {
            return (
              <React.Fragment key={index}>
                {renderGuestList({
                  item,
                  isChild: true,
                })}
              </React.Fragment>
            );
          })}
      </>
    );
  };

  const addGuestPage = () => {
    history.push(
      `/pre-checkin/${propertyUid}/guest-list-management/${bookingNumber}/${encodeURIComponent(
        lastName
      )}/add-guest?language=${language}&email=${encodeURIComponent(userEmail)}`
    );
  };

  const registerGuestPage = (additionalGuestIsPrimary = true) => {
    if (guests.numberOfGuests() === 1 || additionalGuestIsPrimary) {
      history.push(
        `/pre-checkin/${propertyUid}/book-detail/${bookingNumber}/${encodeURIComponent(
          lastName
        )}?language=${language}&email=${encodeURIComponent(userEmail)}`
      );
    } else {
      history.push(
        `/pre-checkin/${propertyUid}/guest-list-management/${bookingNumber}/${encodeURIComponent(
          lastName
        )}/register-guest?language=${language}&email=${encodeURIComponent(
          userEmail
        )}`
      );
    }
  };

  const invitationCount = () =>
    allGuests.reduce(
      (count, guest) =>
        guest.accountExist || guest.mainGuest ? count : count + 1,
      0
    );

  return (
    <>
      <PreCheckInContainer containerName="guest-list-management">
        <PreCheckInHeader
          title={guestService?.title || t("PRE_CHECK_IN", "PRE CHECK-IN")}
          headerLink={() =>
            history.push(
              `/pre-checkin/${propertyUid}/home?language=${language}&token=${accessToken}`
            )
          }
        />
        {booking && guests && (
          <>
            {booking.isBookingActive && (
              <>
                <section
                  id="content"
                  className="relative flex flex-col px-9 w-full flex-1 md:flex-0 pt-6">
                  <div className="flex flex-col">
                    <div className="flex mb-10 w-full">
                      <div
                        className="mr-8 w-24 h-24 md:w-32 md:h-32 rounded-full overflow-hidden flex items-center"
                        style={{
                          backgroundColor: presets?.backgroundColorTopBar,
                        }}>
                        {property.property_system_icon && (
                          <img
                            className="w-full"
                            src={property.property_system_icon}
                            alt={t("HOTEL_LOGO", "Hotel Logo")}
                          />
                        )}
                      </div>
                      <div className="capitalize font-normal">
                        <h1
                          className="mb-2 type-normal large"
                          style={{ color: presets?.titleColor }}>
                          {property.name}
                        </h1>
                        <p
                          className="mb-2 type-regular medium"
                          style={{ color: presets?.subtitleColor }}>
                          {get(booking, "room_description")}
                        </p>
                        <p
                          className="mb-2 type-regular medium"
                          style={{ color: presets?.subtitleColor }}>
                          {t("BOOKING_NUMBER", "Booking Number")}{" "}
                          {get(booking, "booking_id")}
                        </p>
                        <p
                          className="mt-8 type-regular medium"
                          style={{ color: presets?.subtitleColor }}>
                          {configuration.checkPreCheckInInterfaceTypeIsHotelKey
                            ? t("BEDS", "Beds")
                            : t("ROOMS")}
                          :
                          <span className="font-bold pl-1">
                            {get(booking, "number_of_rooms", 1)}
                          </span>{" "}
                          |{" "}
                          {configuration.checkPreCheckInInterfaceTypeIsHotelKey
                            ? t("MAX_GUESTS", "Max Guests")
                            : t("GUESTS")}
                          :
                          <span className="font-bold">
                            {" "}
                            {get(booking, "total_guests", 1)}
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="md:max-w-7xl mx-auto w-full mb-4">
                      <div className="capitalize font-normal flex w-full mb-7">
                        <div className="flex flex-1 justify-end">
                          <div className="flex flex-col space-y-1">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="w-9 h-9 mx-auto mb-4"
                              viewBox="0 0 448 512">
                              <path
                                fill={`${presets?.backgroundColorTopBar}`}
                                d="M148 288h-40c-6.6 0-12-5.4-12-12v-40c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v40c0 6.6-5.4 12-12 12zm108-12v-40c0-6.6-5.4-12-12-12h-40c-6.6 0-12 5.4-12 12v40c0 6.6 5.4 12 12 12h40c6.6 0 12-5.4 12-12zm96 0v-40c0-6.6-5.4-12-12-12h-40c-6.6 0-12 5.4-12 12v40c0 6.6 5.4 12 12 12h40c6.6 0 12-5.4 12-12zm-96 96v-40c0-6.6-5.4-12-12-12h-40c-6.6 0-12 5.4-12 12v40c0 6.6 5.4 12 12 12h40c6.6 0 12-5.4 12-12zm-96 0v-40c0-6.6-5.4-12-12-12h-40c-6.6 0-12 5.4-12 12v40c0 6.6 5.4 12 12 12h40c6.6 0 12-5.4 12-12zm192 0v-40c0-6.6-5.4-12-12-12h-40c-6.6 0-12 5.4-12 12v40c0 6.6 5.4 12 12 12h40c6.6 0 12-5.4 12-12zm96-260v352c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V112c0-26.5 21.5-48 48-48h48V12c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v52h128V12c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v52h48c26.5 0 48 21.5 48 48zm-48 346V160H48v298c0 3.3 2.7 6 6 6h340c3.3 0 6-2.7 6-6z"></path>
                            </svg>
                            <p
                              className="text-okkami-gray text-center type-regular medium uppercase"
                              style={{ color: presets?.subtitleColor }}>
                              {t("CHECK_IN", "CHECK-IN")}
                            </p>
                            <p
                              style={{ color: presets?.titleColor }}
                              className="text-okkami-dark-gray text-center type-title normal uppercase">
                              {booking.checkInDateText}
                            </p>
                            <p
                              className="text-center type-normal normal"
                              style={{ color: presets?.titleColor }}>
                              {`${property.isEvermore ? t("BEGINS_AT", "") : ""} ${presets.amPm
                                ? convertToAmPm(configuration.checkInTimeText)
                                : configuration.checkInTimeText}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex justify-center items-center mx-8 pt-4">
                          <svg
                            className="w-7 h-7 mx-auto"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 448 512">
                            <path
                              fill={`${presets?.backgroundColorTopBar}`}
                              d="M190.5 66.9l22.2-22.2c9.4-9.4 24.6-9.4 33.9 0L441 239c9.4 9.4 9.4 24.6 0 33.9L246.6 467.3c-9.4 9.4-24.6 9.4-33.9 0l-22.2-22.2c-9.5-9.5-9.3-25 .4-34.3L311.4 296H24c-13.3 0-24-10.7-24-24v-32c0-13.3 10.7-24 24-24h287.4L190.9 101.2c-9.8-9.3-10-24.8-.4-34.3z"></path>
                          </svg>
                        </div>
                        <div className="flex flex-1 justify-start">
                          <div className="flex flex-col space-y-1">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="w-9 h-9 mx-auto mb-4"
                              viewBox="0 0 448 512">
                              <path
                                fill={`${presets?.backgroundColorTopBar}`}
                                d="M148 288h-40c-6.6 0-12-5.4-12-12v-40c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v40c0 6.6-5.4 12-12 12zm108-12v-40c0-6.6-5.4-12-12-12h-40c-6.6 0-12 5.4-12 12v40c0 6.6 5.4 12 12 12h40c6.6 0 12-5.4 12-12zm96 0v-40c0-6.6-5.4-12-12-12h-40c-6.6 0-12 5.4-12 12v40c0 6.6 5.4 12 12 12h40c6.6 0 12-5.4 12-12zm-96 96v-40c0-6.6-5.4-12-12-12h-40c-6.6 0-12 5.4-12 12v40c0 6.6 5.4 12 12 12h40c6.6 0 12-5.4 12-12zm-96 0v-40c0-6.6-5.4-12-12-12h-40c-6.6 0-12 5.4-12 12v40c0 6.6 5.4 12 12 12h40c6.6 0 12-5.4 12-12zm192 0v-40c0-6.6-5.4-12-12-12h-40c-6.6 0-12 5.4-12 12v40c0 6.6 5.4 12 12 12h40c6.6 0 12-5.4 12-12zm96-260v352c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V112c0-26.5 21.5-48 48-48h48V12c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v52h128V12c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v52h48c26.5 0 48 21.5 48 48zm-48 346V160H48v298c0 3.3 2.7 6 6 6h340c3.3 0 6-2.7 6-6z"></path>
                            </svg>
                            <p
                              className="text-okkami-gray font-normal text-center type-regular medium uppercase"
                              style={{ color: presets?.subtitleColor }}>
                              {t("CHECK_OUT", "CHECK-OUT")}
                            </p>
                            <p
                              style={{ color: presets?.titleColor }}
                              className="text-okkami-dark-gray text-center type-title normal uppercase">
                              {booking.checkOutDateText}
                            </p>
                            <p
                              className="text-center type-normal normal"
                              style={{ color: presets?.titleColor }}>
                              {presets.amPm
                                ? convertToAmPm(configuration.checkOutTimeText)
                                : configuration.checkOutTimeText}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="text-center">
                        <p className="mb-4">
                          {!booking.mainGuestIsPrimaryGuest
                            ? t("ACCOMPANYING_GUEST_DESCRIPTION")
                            : t("GUEST_LIST_MANAGEMENT_INSTRUCTION")}
                        </p>
                        {booking.guestReachLimit ||
                        !booking.mainGuestIsPrimaryGuest ||
                        !enableRegisterDuringWindowTime() ||
                        guests.numberOfGuests() ===
                          booking.totalGuests ? null : (
                          <button
                            className={`bg-white app-outline-button type-title medium ${presets.buttonStyleName} capitalize`}
                            style={{
                              backgroundColor: presets?.backgroundColorTopBar,
                              color: presets?.appHomeTextDetailsPrecheckin,
                            }}
                            onClick={() => addGuestPage()}>
                            {t("ADD_GUEST")}
                          </button>
                        )}
                      </div>

                      <hr
                        style={{
                          marginTop: "20px",
                          marginBottom: "20px",
                          borderColor: presets?.appHomeRowDividerColor,
                        }}
                      />

                      <div>
                        <p className="capitalize">{`${t(
                          "GUESTS"
                        )} (${guests.numberOfGuests()} ${t("OF")} ${
                          booking.totalGuests
                        })`}</p>
                      </div>

                      <div className="w-full mb-4 py-8 relative">
                        {allGuests.map((item, index) => (
                          <React.Fragment key={index}>
                            {renderGuestList({
                              item,
                              invitationList: false,
                            })}
                          </React.Fragment>
                        ))}
                        {invitationCount() > 0 && (
                          <>
                            <div>
                              <p className="capitalize">
                                {t("INVITATIONS_SENT")}
                                <span
                                  className="bg-white border rounded-full px-4 w-32 py-2 type-title normal ml-4"
                                  style={{
                                    color: presets.appHomeTextDetailsPrecheckin,
                                    backgroundColor:
                                      presets.backgroundColorTopBar,
                                  }}>
                                  {invitationCount()}
                                </span>
                              </p>
                            </div>
                            {allGuests.map((item, index) => (
                              <React.Fragment key={index}>
                                {renderGuestList({
                                  item,
                                  invitationList: true,
                                })}
                              </React.Fragment>
                            ))}
                          </>
                        )}
                      </div>
                    </div>
                    {(((booking.guestReachLimit ||
                      !booking.mainGuestIsPrimaryGuest) &&
                      enableRegisterDuringWindowTime()) ||
                      guests.numberOfGuests() === booking.totalGuests) && (
                      <button
                        className={`app-button-ipad type-title medium ${presets.buttonStyleName}`}
                        style={{
                          backgroundColor: presets.backgroundColorTopBar,
                          color: presets.appHomeTextCallToAction,
                        }}
                        onClick={() =>
                          booking.mainGuestIsPrimaryGuest
                            ? registerGuestPage(false)
                            : goToBookDetail()
                        }>
                        {t("CONTINUE")}
                      </button>
                    )}
                  </div>
                </section>
                {(((booking.guestReachLimit ||
                  !booking.mainGuestIsPrimaryGuest) &&
                  enableRegisterDuringWindowTime()) ||
                  guests.numberOfGuests() === booking.totalGuests) && (
                  <button
                    className="app-button-mobile type-title medium "
                    style={{
                      backgroundColor: presets.backgroundColorTopBar,
                      color: presets.appHomeTextCallToAction,
                      position: "fixed",
                      bottom: 0,
                      left: 0,
                    }}
                    onClick={() =>
                      booking.mainGuestIsPrimaryGuest
                        ? registerGuestPage()
                        : goToBookDetail()
                    }>
                    {t("CONTINUE")}
                  </button>
                )}
              </>
            )}

            {!booking.isBookingActive && (
              <section
                id="content"
                className="relative flex flex-col px-9 w-full flex-1 md:flex-0">
                <div className="flex flex-col justify-center">
                  {guestService.bookingInfoImage && (
                    <div className="-mx-9">
                      <BookingInfoImage
                        bookingInfoImage={guestService.bookingInfoImage}
                        propertyName={property.name}
                      />
                    </div>
                  )}
                  <div className="flex-1 w-full flex flex-col p-6">
                    <div
                      className="w-full mb-20 rounded-3xl h-72 p-8 shadow-lg relative max-w-2xl mx-auto"
                      style={{
                        backgroundColor: presets?.backgroundColorTopBar,
                        height: "26rem",
                      }}>
                      <div className="flex flex-col">
                        <div className="text-center pb-6">
                          <p
                            className="type-title normal"
                            style={{
                              color: presets?.appHomeTextDetailsPrecheckin,
                            }}>
                            {property.name}
                          </p>
                        </div>
                        <hr style={{ backgroundColor: "#DBDBDB" }} />
                        <div className="flex justify-between text-12 py-10">
                          <p
                            className="type-title normal"
                            style={{
                              color: presets?.appHomeTextDetailsPrecheckin,
                            }}>
                            {t("DATE")}
                          </p>
                          <p
                            className="text-white type-regular normal"
                            style={{
                              color: presets?.appHomeTextDetailsPrecheckin,
                            }}>
                            {booking.checkInDateText} -{" "}
                            {booking.checkOutDateText}
                          </p>
                        </div>
                        <hr style={{ backgroundColor: "#DBDBDB" }} />
                        <div className="flex justify-between text-12 py-10">
                          <p
                            className="type-title normal"
                            style={{
                              color: presets?.appHomeTextDetailsPrecheckin,
                            }}>
                            {t("BOOKING_NO", "BOOKING NO.")}
                          </p>
                          <p
                            className="text-white type-regular normal"
                            style={{
                              color: presets?.appHomeTextDetailsPrecheckin,
                            }}>
                            {bookingNumber}
                          </p>
                        </div>
                        <hr style={{ backgroundColor: "#DBDBDB" }} />
                        <div className="flex justify-between text-12 py-10">
                          <p
                            className="type-title normal"
                            style={{
                              color: presets?.appHomeTextDetailsPrecheckin,
                            }}>
                            {t("ROOM_TYPE", "ROOM TYPE")}
                          </p>
                          <p
                            className="text-white type-regular normal"
                            style={{
                              color: presets?.appHomeTextDetailsPrecheckin,
                            }}>
                            {booking.room_description}
                          </p>
                        </div>
                      </div>
                      {[
                        "pre_check_in_completed",
                        "pre_checkin_completed",
                      ].includes(booking.status) && (
                        <div className="text-center">
                          <button
                            className={`bg-white app-outline-button type-title medium ${presets.buttonStyleName}`}
                            style={{
                              color: presets?.backgroundColorTopBar,
                              borderColor: presets?.backgroundColorTopBar,
                            }}
                            onClick={() => this.confirmationPage()}>
                            {t("CHECK_IN_COMPLETED")}
                          </button>
                        </div>
                      )}
                      {booking.status === "room_is_ready" && (
                        <div className="text-center mx-auto">
                          <button
                            className={`bg-white app-outline-button type-title medium ${presets.buttonStyleName}`}
                            style={{
                              color: presets?.backgroundColorTopBar,
                              borderColor: presets?.backgroundColorTopBar,
                            }}
                            onClick={() => this.confirmationPage()}>
                            {t("ROOM_IS_READY")}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </section>
            )}
          </>
        )}
        {pageLoading && (
          <div className="fixed inset-0 flex justify-center items-center bg-gray-500 opacity-50 z-50">
            <ClipLoaderIcon
              loadingState={pageLoading}
              size={30}
              color={presets.backgroundColorTopBar}
            />
          </div>
        )}
      </PreCheckInContainer>
    </>
  );
};

export default withTranslation()(GuestListManagement);
